// @Deprecated: This file is deprecated and will be removed in the future.
// TODO LEGACY DEPRECATION: Remove this file when deprecating legacy app

// Third-party
import React, { Component } from 'react';
import debounce from 'lodash/debounce';

// Utils
import { FormattedMessage, formatMessage } from '../../../../../utils/i18n';
import { truncateString } from '../../../../../utils/objects';
import { CONTACT_LAYOUT, getContactLayout } from '../../../../../utils/contacts';

// Components
import ConnectedPrivateComponent from '../../../../components/PrivateComponent';
import { SearchInput } from '../../../../components/inputs';
import { PrimaryButton } from '../../../../components/buttons';
import { ComparisonsTable } from '../../../../components/tables';
import { GenericEmptyState } from '../../../../components/misc';
import { LoadingSpinner } from '../../../../components/common';
import { PERMISSIONS, userHasPermission } from '../../../../../utils/permissions';
import { ComparisonsTableFields } from '../../../../components/tables/ComparisonsTable';
import User from '@/types/user';
import ReducedComparison from '@/types/reduced-comparison';
import GenericContact from '@/types/generic-contact';
import Comparison from '@/types/comparison';

type Props = {
  user: User;
  onSearch: (query: string) => Promise<ReducedComparison[]>;
  contact: GenericContact;
  comparisons: Comparison[];
  nextContactComparisonsToken?: string;
  onLoadMore: (query?: string) => void;
  hasMoreComparisons: boolean;
  loading: boolean;
  onCreateComparison: () => void;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onViewComparison: (comparison: ReducedComparison) => void;
  onRerunComparison: (comparison: ReducedComparison) => void;
  openingComparisonIds?: string[];
  rerunningComparisonIds?: string[];
};

type State = {
  query: string;
};

export default class ContactComparisons extends Component<Props, State> {
  state = {
    query: '',
  };

  componentWillUnmount() {
    this.clearSearch();
  }

  searchComparisons = debounce(() => {
    this.props.onSearch(this.state.query);
  }, 250);

  onSearch = (query: string) => {
    this.setState({ query });
    this.searchComparisons();
  };

  clearSearch = () => {
    this.setState({ query: '' });
    this.props.onSearch('');
  };

  renderEmptyState = () => {
    const { user } = this.props;
    const layout = getContactLayout(user);

    const emptyStateHeader = formatMessage(`EmptyState.NoContactComparisons.${layout === CONTACT_LAYOUT.CLIENT ? 'ClientTitle' : 'ContactTitle'}`);
    let emptyStateBody: string | undefined = formatMessage(
      `EmptyState.NoContactComparisons.${layout === CONTACT_LAYOUT.CLIENT ? 'ClientText' : 'ContactText'}`,
    );

    const canAdd = userHasPermission(user, PERMISSIONS.COMPARISONS_CREATE);
    if (!canAdd) {
      emptyStateBody = undefined;
    }

    return (
      <GenericEmptyState
        header={emptyStateHeader}
        body={emptyStateBody}
        actionLabel={canAdd ? formatMessage('EmptyState.NoContactComparisons.GetStarted') : undefined}
        actionIcon="icon-arrow-up-right2"
        onAction={this.props.onCreateComparison}
      />
    );
  };

  renderNoResultsState = () => (
    <GenericEmptyState
      header={formatMessage('EmptyState.NoResults.Title')}
      body={formatMessage('EmptyState.NoResults.Text')}
      actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
      onAction={this.clearSearch}
    />
  );

  renderLoadingState = () => <LoadingSpinner className="initial-loading" message={formatMessage('Contacts.ContactTabs.Comparisons.LoadingComparisons')} />;

  onLoadMore = () => {
    const { query } = this.state;
    this.props.onLoadMore(query);
  };

  renderContent = () => {
    const { comparisons, loading, user, openingComparisonIds, rerunningComparisonIds } = this.props;
    const { query } = this.state;

    const showEmptyState = comparisons.length === 0 && !loading && !query;
    if (showEmptyState) {
      return this.renderEmptyState();
    }

    const showNoResultsState = comparisons.length === 0 && !loading && query;
    if (showNoResultsState) {
      return this.renderNoResultsState();
    }

    const showLoadingState = comparisons.length === 0 && loading;
    if (showLoadingState) {
      return this.renderLoadingState();
    }

    return (
      <ComparisonsTable
        user={user}
        fields={[
          ComparisonsTableFields.Name,
          ComparisonsTableFields.LastModified,
          ComparisonsTableFields.Open,
          ComparisonsTableFields.Rerun,
          ComparisonsTableFields.Actions,
        ]}
        comparisons={comparisons}
        onOpenComparison={this.props.onViewComparison}
        onRerunComparison={this.props.onRerunComparison}
        onDeleteComparison={this.props.onDeleteComparison}
        onLoadMore={this.onLoadMore}
        hasMore={this.props.hasMoreComparisons}
        scrollAncestor=".app-scrollbar-container"
        openingComparisonIds={openingComparisonIds}
        rerunningComparisonIds={rerunningComparisonIds}
      />
    );
  };

  render() {
    const { contact, loading } = this.props;
    const { query } = this.state;

    return (
      <div className="contact-comparisons">
        <div className="content-header">
          <h1 data-private data-dd-privacy="mask">
            <FormattedMessage id="Contacts.ContactTabs.Comparisons.Header" values={{ name: truncateString(contact.fname || '', 80) }} />
          </h1>

          <div className="content-actions">
            <ConnectedPrivateComponent permissions={[PERMISSIONS.COMPARISONS_FILTER]}>
              {() => (
                <SearchInput
                  loading={loading}
                  onChange={this.onSearch}
                  value={query}
                  placeholder={formatMessage('Contacts.ContactTabs.Comparisons.SearchPlaceholder')}
                />
              )}
            </ConnectedPrivateComponent>

            <ConnectedPrivateComponent permissions={[PERMISSIONS.COMPARISONS_CREATE]}>
              {() => (
                <PrimaryButton icon="ds-add" onClick={this.props.onCreateComparison}>
                  <FormattedMessage id="Contacts.ContactTabs.Comparisons.CreateComparison" />
                </PrimaryButton>
              )}
            </ConnectedPrivateComponent>
          </div>
        </div>

        {this.renderContent()}
      </div>
    );
  }
}
